import * as React from 'react';
import assertions from 'lib/assertions';
import Row from 'components/Row';
import Columns2 from 'components/Columns2';
// @ts-ignore
import Countries, { regionsForCountry } from 'lib/geo/Country';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import { OrganizationType } from 'globalTypes';
import Select from 'components/Select';
import OrganizationPickerContainer from 'components/OrganizationPicker/OrganizationPickerContainer';
import ToastActions from 'actions/ToastActions';
import { baseTrackingPrefix } from 'constants/SelfServeConstants';
import { badPostalCodeForCountry } from 'utils/selfServeHelpers';
import './styles/AdminUpgradePlanInfoInputForm.scss';

type FormFields = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  studentsCount?: number;
  teachersCount?: number;
  comment?: string;
};

export type Props = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  orgUuid?: string;
  organizationName?: string;
  studentsCount?: number;
  teachersCount?: number;
  comment?: string;
  onUpdate: (state: FormFields, badPostalCode: boolean) => void;
};

type State = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  orgUuid?: string;
  organizationName?: string;
  orgType?: OrganizationType;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  showAddressForm: boolean;
  errors: { [key: string]: string | undefined };
};

class AdminUpgradePlanOrgInfoInteractive extends React.Component<Props, State> {
  readonly state: State;
  private readonly trackingPrefix = `${baseTrackingPrefix}.admin_upgrade_interactive_form`;

  constructor(props: Props) {
    super(props);
    const { onUpdate, ...stateProps } = props;
    this.state = {
      ...stateProps,
      showAddressForm: false,
      errors: {},
    };
  }

  populateOrgAddressState = (orgInfo: any) => {
    const updates: State = {
      orgUuid: orgInfo.uuid,
      lat: Number.isNaN(Number.parseFloat(orgInfo.latitude))
        ? undefined
        : Number.parseFloat(orgInfo.latitude),
      long: Number.isNaN(Number.parseFloat(orgInfo.longitude))
        ? undefined
        : Number.parseFloat(orgInfo.longitude),
      country: orgInfo.country_code,
      state: orgInfo.state,
      city: orgInfo.city,
      streetAddress: orgInfo.street,
      postalCode: orgInfo.zip,
      orgType: orgInfo.type,
      showAddressForm: true,
      errors: { ...this.state.errors },
    };
    this.updateState(updates, badPostalCodeForCountry(updates.postalCode, updates.country));
  };

  renderOrganizationPickerForm = () => {
    return (
      <FormElement
        label={'Organization name'}
        onValidChange={orgInfo => this.populateOrgAddressState(orgInfo)}
        element={OrganizationPickerContainer}
        elementProps={{
          onError: () =>
            ToastActions.showErrorToast(
              'There was a problem searching for your Organization. Please try again'
            ),
        }}
        data-test="organization-picker"
        manualError={this.state.errors['organization']}
      />
    );
  };

  updateState = (update: FormFields, noPostalCodeMatch?: boolean) => {
    this.setState(update);
    this.props.onUpdate(update, noPostalCodeMatch || false);
  };

  renderAddressForm = () => {
    const regionLabel = regionsForCountry[this.state.country]
      ? regionsForCountry[this.state.country].label
      : 'Region';
    return (
      <div data-test="address-form">
        <FormElement
          label={'Country'}
          validate={value => {
            assertions.notEmpty(value, 'Please select a country');
            assertions.validPostalCodeForCountry(this.state.postalCode || '', value);
          }}
          onValidChange={country => {
            const postalCodeError = badPostalCodeForCountry(this.state.postalCode, country);
            this.updateState({ country: postalCodeError ? undefined : country }, postalCodeError);
          }}
          onInvalidChange={country => {
            const postalCodeError = badPostalCodeForCountry(this.state.postalCode, country);
            this.updateState({ country: undefined }, postalCodeError);
          }}
          element={Select}
          elementProps={{
            name: 'country',
            value: this.state.country,
            options: Countries,
            className: 'AdminUpgradePlanInfoInputForm-countryPicker',
          }}
        />
        <Columns2>
          <FormElement
            label={'Street address'}
            validate={address => {
              assertions.notEmpty(address);
            }}
            onValidChange={e => this.updateState({ streetAddress: e.target.value })}
            onInvalidChange={() => this.updateState({ streetAddress: undefined })}
            element={Input}
            elementProps={{
              value: this.state.streetAddress ?? '',
              placeholder: '1234 Remind Road',
              'data-test-selector': 'street-address-input',
            }}
          />
          <FormElement
            label={'City'}
            validate={city => {
              assertions.notEmpty(city);
            }}
            onValidChange={e => this.updateState({ city: e.target.value })}
            onInvalidChange={() => this.updateState({ city: undefined })}
            element={Input}
            elementProps={{
              value: this.state.city,
              'data-test-selector': 'city-input',
            }}
          />
        </Columns2>
        <Columns2>
          <FormElement
            label={regionLabel}
            onValidChange={state => this.updateState({ state: state })}
            onInvalidChange={() => this.updateState({ state: undefined })}
            validate={value => {
              assertions.notEmpty(value, `Please select a ${regionLabel.toLowerCase()}`);
            }}
            element={Select}
            elementProps={{
              name: 'state',
              value: this.state.state,
              options: regionsForCountry[this.state.country]
                ? regionsForCountry[this.state.country].regions
                : [],
              disabled: !this.state.country,
              className: 'AdminUpgradePlanInfoInputForm-regionPicker',
            }}
          />
          <FormElement
            label={'Postal code'}
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.validPostalCodeForCountry(
                e.target.value,
                this.state.country || '',
                'Invalid postal code'
              );
            }}
            onValidChange={e => {
              const postalCodeError = badPostalCodeForCountry(e.target.value, this.state.country);
              this.updateState(
                { postalCode: postalCodeError ? undefined : e.target.value },
                postalCodeError
              );
            }}
            onInvalidChange={e => {
              const postalCodeError = badPostalCodeForCountry(e.target.value, this.state.country);
              this.updateState({ postalCode: undefined }, postalCodeError);
            }}
            element={Input}
            elementProps={{
              name: 'postal code',
              value: this.state.postalCode,
              'data-test-selector': 'postal-code-input',
            }}
            manualError={
              badPostalCodeForCountry(this.state.postalCode || '', this.state.country || '')
                ? 'Invalid postal code'
                : undefined
            }
          />
        </Columns2>
      </div>
    );
  };

  renderCountsAndCommentForm = () => {
    return (
      <div>
        <Row size="large">
          <FormElement
            label={'Why are you interested in Remind Hub?'}
            validate={comment => {
              assertions.notEmpty(comment);
            }}
            onValidChange={e => this.updateState({ comment: e.target.value })}
            onInvalidChange={() => this.updateState({ comment: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.comment,
              placeholder:
                "Let us know why you're considering Remind and any questions you might have.",
            }}
          />
        </Row>
        <Columns2>
          <FormElement
            label={'Estimated teachers on staff'}
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e =>
              this.updateState({
                teachersCount: Number.isNaN(Number.parseInt(e.target.value))
                  ? 0
                  : Number.parseInt(e.target.value),
              })
            }
            onInvalidChange={() => this.updateState({ teachersCount: undefined })}
            element={Input}
            elementProps={{
              defaultValue: this.state.teachersCount,
              'data-test-selector': 'est-teachers-input',
            }}
          />
          <FormElement
            label={'Estimated student enrollment'}
            validate={e => {
              assertions.number(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e =>
              this.updateState({ studentsCount: Number.parseInt(e.target.value, 10) })
            }
            onInvalidChange={() => this.updateState({ studentsCount: undefined }, false)}
            element={Input}
            elementProps={{
              defaultValue: this.props.studentsCount,
              'data-test-selector': 'est-students-input',
            }}
          />
        </Columns2>
      </div>
    );
  };

  render = () => {
    return (
      <div data-test="about-org-form-interactive">
        {this.renderOrganizationPickerForm()}
        {this.state.showAddressForm && this.renderAddressForm()}
        {this.renderCountsAndCommentForm()}
      </div>
    );
  };
}

export { AdminUpgradePlanOrgInfoInteractive };
export default AdminUpgradePlanOrgInfoInteractive;
